/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

import _ from 'lodash';
// eslint-disable-next-line import/no-unresolved
import { v4 as uuidv4 } from 'uuid';
import { notification } from 'antd';
import arClient from '../../util/api_client';
import { OK, LEGACY_OK, isApiSuccess } from '../../constants/platformStatusCodes';
import stripFroalaAttribution from '../../util/stripFroalaAttribution';
import { BRAND_SAFETY_LABELS } from '../Persona/BrandSafety/brandSafetyConsts';
import { OUTREACH_DEFAULT_EMAIL } from './Outreach/constants';
import { normalizeSenderPlatform } from './Outreach/OutreachUtils';
import { processReportingData } from './Dashboard/Reporting/reportingUtils';

const initialState = {
  campaignList: [],
  campaignListLoading: false,
  campaignAccessError: null,
  ROIDashboardCampaignData: {},
  ROIDashboardData: {},
  ROIDashboardDataUpdated: null,
  ROIDashboardDataLoading: false,
  ReportingDataResponce: {},
  ReportingDataResponceV2: {},
  ReportingDataInterests: [],
  ReportingDataCapsules: [],
  ReportingData: {},
  ReportingDataLoading: false,
  ReportingDataLoadingCapsules: false,
  isCreateCampaignLoading: false,
  latestSavedCampaign: null,
  errorMsg: null,
  campaignCreators: [],
  isCampaignCreatorsFetched: false,
  fetchCampaignCreatorsLoading: false,
  isCampaignDashboardLoading: false,
  isOutreachBudgetLoading: false,
  outreachBudgetData: {},
  outreachNotesData: {},
  isOutreachNotesLoading: false,
  outreachData: {
    contactDetails: [],
    emails: [],
    outreachId: null,
  },
  creativeBriefVisible: false,
  NDAVisible: false,
  revisionDetailsData: {
    campaignAndCreatorDetails: [],
    revisionDetails: {},
  },
  influencerAgreementCountData: {},
  isInfluencerAgreementCountLoading: false,
  outreachEmailAddresses: [],
  isOutreachEmailAddressesLoading: false,
  tikTokSparkAds: null,
  campaignCreatorsFetchedId: null,
  emailMassOutreachList: [],
  activeSection: 'content',
  docLibCreateDocType: null,
  docLibTemplateEditorMode: null, // 'view' | 'edit' | 'create'
  docLibActiveTemplate: null, // template Record || null,
  docLibTemplates: [],
  campaignOverview: {},
  docLibGoogleDocsEditorMode: null, // 'edit' | 'create' | 'upload
  docLibGoogleDocSelected: null, // google doc Record || null,
  documentSendConfirmation: {
    visible: false,
    documentType: null,
    documentName: null,
    recipientName: null,
  },
};

export const defaultDeliverables = {
  tiktok: {
    video: 10,
  },
  twitter: {
    post: 0,
  },
  instagram: {
    post: 0,
    reels: 0,
    story: 0,
  },
  youtube: {
    shorts: 0,
    video: 0,
  },
};

export const defaultTermsOrganic = {
  tiktok: 90,
  twitter: 90,
  instagram: 90,
  youtube: 90,
};

export const defaultTermsPaid = {
  tiktok: 7,
  twitter: 7,
  instagram: 7,
  youtube: 7,
};

export const defaultCampaignFormValues = {
  flight_end: '',
  flight_start: '',
  flight_tz: 'America/Los_Angeles',
  country: 'US',
  region: 'California',
  id: '',
  name: '',
  status: '',
  budget: '',
  // campaignDetails
  campaignDescription: '',
  productName: '',
  productDescription: '',
  videoGuidelines: '',
  creatorBrief: '',
  deliverables: defaultDeliverables,
  // NEW Campaign Data
  successDefinition: '',
  campaignGoals: '',
  campaignObjectives: '',
  objectiveDetails: '',

  // Creator Brief
  creatorsAmount: '',
  priceRange_min: 10,
  priceRange_max: 100,
  videoDuration_min: null,
  videoDuration_max: null,
  briefMusicNeeded: '',
  briefCreatorShouldDo: '',
  briefCreatorShouldAvoid: '',
  briefTechRequiroments: '',
  briefCaptions: '',
  briefHashtags: '',
  brandAssets: [],
  brandAssetsSubmited: [],
  CTA: '',

  // Usage Rights
  termsOrganic: defaultTermsOrganic,
  termsPaid: defaultTermsPaid,

  // FTC
  ftcLink: '',
  ftcAssets: [],
  ftcAssetsSubmited: [],
  COPPA: false,
  alcohol: false,
  pharmaISI: false,

  // campaignBrandDetails
  brandName: '',
  brandDescription: '',
  brandInstagramLink: '',
  brandYoutubeLink: '',
  brandTwitterLink: '',
  brandTiktokLink: '',
  brandFacebookLink: '',
  brandLinkedinLink: '',
  customSocialLinks: [],
  brandURL: '',
  competitorData: [{ name: '', URL: '' }],
  categories: {},

  // Spark AD Authorization
  sparkAdsRequestsd: false,
  sparkAdsID: '',
  advertiserIDs: null,
  advertiserIdsLoading: false,

  // Docusign
  envelopesList: [],
  envelopesListLoading: false,

  // Google Docs
  googleDocumentList: [],
  googleDocumentListLoading: false,
  googleDocsAccount: null,

  // init brand safety config with all flags enabled
  brandSafetyFlagSetting: _.mapValues(BRAND_SAFETY_LABELS, () => true),

  // e-commerce data
  ecommerceCategoriesList: [],
  ecommerceCategoriesListLoading: false,
};

const CampaignsSlice = createSlice({
  name: 'Campaigns',
  initialState,
  reducers: {
    resetAll(state) {
      state.campaignList = initialState.campaignList;
    },
    setCampaignList(state, action) {
      const {
        payload: { list },
      } = action;
      state.campaignList = list;
    },
    setCampaignListLoading(state, action) {
      const { payload } = action;
      state.campaignListLoading = payload;
    },
    setCampaignAccessError(state, action) {
      const { payload } = action;
      state.campaignAccessError = payload;
    },
    setROIDashboardData(state, action) {
      state.ROIDashboardData = action.payload;
    },
    setROIDashboardCampaignData(state, action) {
      const {
        payload: { campaign },
      } = action;
      state.ROIDashboardCampaignData = campaign;
    },
    setROIDashboardForecastBudgetData(state, action) {
      const { payload } = action;
      state.ROIDashboardData.forecast_suggestedMonthlySpend = payload;
    },
    setROIDashboardDataLoading(state, action) {
      const { payload } = action;
      state.ROIDashboardDataLoading = payload;
    },
    setROIDashboardDataUpdated(state, action) {
      const { payload } = action;
      state.ROIDashboardDataUpdated = payload;
    },
    setIsCreateCampaignLoading(state, action) {
      const { payload } = action;
      state.isCreateCampaignLoading = payload;
    },
    setLatestSavedCampaign(state, action) {
      const { payload } = action;
      state.latestSavedCampaign = payload;
    },
    setErrMsg(state, action) {
      const { payload } = action;
      state.errorMsg = payload;
    },
    setCampaignCreatorData(state, action) {
      const { payload } = action;
      state[payload.key] = payload.value;
    },
    setCampaignDashboardLoading(state, action) {
      const { payload } = action;
      state.isCampaignDashboardLoading = payload;
    },
    setOutreachBudgetData(state, action) {
      const { payload } = action;
      state.outreachBudgetData = payload;
    },
    setOutreachBudgetDataLoading(state, action) {
      const { payload } = action;
      state.isOutreachBudgetLoading = payload;
    },
    setOutreachNotesData(state, action) {
      const { payload } = action;
      state.outreachNotesData = payload;
    },
    setOutreachNotesDataLoading(state, action) {
      const { payload } = action;
      state.isOutreachNotesLoading = payload;
    },
    setOutreachData(state, action) {
      state.outreachData = action.payload;
    },
    resetOutreachData(state) {
      state.outreachData = {
        contactDetails: [],
        emails: [],
        outreachId: null,
      };
    },
    setInfluencerAgreementCount(state, action) {
      const { payload } = action;
      state.influencerAgreementCountData = payload;
    },
    setInfluencerAgreementCountLoading(state, action) {
      const { payload } = action;
      state.isInfluencerAgreementCountLoading = payload;
    },
    setRevisionDetailsData(state, action) {
      state.revisionDetailsData = action.payload;
    },
    setOutreachEmailAddressesData(state, action) {
      state.outreachEmailAddresses = Array.isArray(action.payload) ? action.payload : [];
    },
    setOutreachEmailAddressesDataLoading(state, action) {
      state.isOutreachEmailAddressesLoading = action.payload;
    },
    setTikTokSparkAds(state, action) {
      const { payload } = action;
      state.tikTokSparkAds = payload;
    },
    setAdvertiserIDList(state, action) {
      const { payload } = action;
      state.advertiserIDs = payload;
    },
    setAdvertiserSparkLoading(state, action) {
      const { payload } = action;
      state.advertiserIdsLoading = payload;
    },
    setEnvelopesList(state, action) {
      const { payload } = action;
      state.envelopesList = payload;
    },
    setEnvelopesListLoading(state, action) {
      const { payload } = action;
      state.envelopesListLoading = payload;
    },
    setGoogleDocumentList(state, action) {
      const { payload } = action;
      state.googleDocumentList = payload;
    },
    setGoogleDocumentListLoading(state, action) {
      const { payload } = action;
      state.googleDocumentListLoading = payload;
    },
    setGoogleDocsAccount(state, action) {
      state.googleDocsAccount = action.payload;
    },

    setReportingDataResponce(state, action) {
      state.ReportingDataResponce = action.payload;
    },
    setReportingDataResponceV2(state, action) {
      state.ReportingDataResponceV2 = action.payload;
    },
    setReportingDataInterests(state, action) {
      state.ReportingDataInterests = action.payload;
    },
    setReportingDataCapsules(state, action) {
      state.ReportingDataCapsules = action.payload;
    },
    setReportingData(state, action) {
      state.ReportingData = action.payload;
    },
    setReportingDataLoading(state, action) {
      state.ReportingDataLoading = action.payload;
    },
    setReportingDataLoadingCapsules(state, action) {
      state.ReportingDataLoadingCapsules = action.payload;
    },
    setEcommerceCategoriesList(state, action) {
      state.ecommerceCategoriesList = action.payload;
    },
    setEcommerceCategoriesListLoading(state, action) {
      state.ecommerceCategoriesListLoading = action.payload;
    },
    setEmailMassOutreachList(state, action) {
      state.emailMassOutreachList = action.payload;
    },
    setActiveSection(state, action) {
      state.activeSection = action.payload;
    },
    setCreativeBriefVisible(state, action) {
      state.creativeBriefVisible = action.payload;
    },
    setDocLibCreateDocType(state, action) {
      state.docLibCreateDocType = action.payload;
    },
    setDocLibTemplateEditorMode(state, action) {
      state.docLibTemplateEditorMode = action.payload;
    },
    setDocLibActiveTemplate(state, action) {
      state.docLibActiveTemplate = action.payload;
    },
    setCampaignOverview(state, action) {
      state.campaignOverview = action.payload;
    },
    setNDAVisible(state, action) {
      state.NDAVisible = action.payload;
    },
    setDocLibTemplates(state, action) {
      state.docLibTemplates = action.payload;
    },
    addDocLibTemplate(state, action) {
      state.docLibTemplates.push(action.payload);
    },
    updateDocLibTemplate(state, action) {
      const { id, updates } = action.payload;
      const index = state.docLibTemplates.findIndex((t) => t.id === id);
      if (index !== -1) {
        state.docLibTemplates[index] = {
          ...state.docLibTemplates[index],
          ...updates,
        };
      }
    },
    setDocLibGoogleDocsEditorMode(state, action) {
      state.docLibGoogleDocsEditorMode = action.payload;
    },
    setDocLibGoogleDocSelected(state, action) {
      state.docLibGoogleDocSelected = action.payload;
    },
    showDocumentSendConfirmation(state, action) {
      state.documentSendConfirmation = { visible: true, ...action.payload };
    },
    clearDocumentSendConfirmation(state) {
      state.documentSendConfirmation = {
        visible: false,
        documentType: null,
        documentName: null,
        recipientName: null,
      };
    },
  },
});

export const {
  resetAll,
  setCampaignList,
  setROIDashboardData,
  setROIDashboardCampaignData,
  setROIDashboardDataLoading,
  setROIDashboardForecastBudgetData,
  setCampaignListLoading,
  setCampaignAccessError,
  setROIDashboardDataUpdated,
  setIsCreateCampaignLoading,
  setLatestSavedCampaign,
  setErrMsg,
  setCampaignCreatorData,
  setCampaignDashboardLoading,
  setOutreachBudgetData,
  setOutreachBudgetDataLoading,
  setOutreachNotesData,
  setOutreachNotesDataLoading,
  setOutreachData,
  resetOutreachData,
  setInfluencerAgreementCount,
  setInfluencerAgreementCountLoading,
  setRevisionDetailsData,
  setOutreachEmailAddressesData,
  setOutreachEmailAddressesDataLoading,
  setTikTokSparkAds,
  setAdvertiserIDList,
  setAdvertiserSparkLoading,
  setEnvelopesList,
  setEnvelopesListLoading,
  setGoogleDocumentList,
  setGoogleDocumentListLoading,
  setGoogleDocsAccount,
  setReportingDataResponce,
  setReportingDataResponceV2,
  setReportingDataInterests,
  setReportingDataCapsules,
  setReportingData,
  setReportingDataLoading,
  setReportingDataLoadingCapsules,
  setEcommerceCategoriesList,
  setEcommerceCategoriesListLoading,
  setEmailMassOutreachList,
  setActiveSection,
  setCreativeBriefVisible,
  setCampaignOverview,
  setNDAVisible,
  setDocLibCreateDocType,
  setDocLibTemplateEditorMode,
  setDocLibActiveTemplate,
  setDocLibTemplates,
  addDocLibTemplate,
  updateDocLibTemplate,
  setDocLibGoogleDocsEditorMode,
  setDocLibGoogleDocSelected,
  showDocumentSendConfirmation,
  clearDocumentSendConfirmation,
} = CampaignsSlice.actions;

const displayNotification = (type, msg, dsc) => {
  notification[type]({
    title: msg,
    duration: 10,
    maxCount: 2,
    description: dsc,
  });
};

export const resetAllCampaigns = () => async (dispatch) => {
  dispatch(resetAll());
};

export const getCampaignList = (campaignId = null) => async (dispatch) => {
  dispatch(setCampaignListLoading(true));

  if (campaignId) {
    // When editing a single campaign, use get-overview which returns full details
    const callback = (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        const { campaign, campaignDetails, campaignBrandDetails } = respApi.data;
        // Format as list with single campaign including full details
        dispatch(
          setCampaignList({
            list: [
              {
                ...campaign,
                id: String(campaign.id),
                campaignDetails,
                campaignBrandDetails,
              },
            ],
            status: [],
          })
        );
      }
      dispatch(setCampaignListLoading(false));
    };
    arClient.doApiCall('/campaign/get-overview', { campaignId }, callback);
  } else {
    // When listing all campaigns, use lightweight endpoint
    const callback = (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        dispatch(setCampaignList(respApi.data));
      }
      dispatch(setCampaignListLoading(false));
    };
    arClient.doApiCall('/campaign/listLite', {}, callback);
  }
};

export const pauseCampaign = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const callback = (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        const newStatus = respApi.data?.newStatus || 'Paused';
        dispatch(
          setCampaignList({
            list: getState().Campaigns.campaignList.map((campaign) =>
              // eslint-disable-next-line eqeqeq
              campaign.id == campaignId ? { ...campaign, status: newStatus } : campaign
            ),
          })
        );
        resolve(respApi.data);
      } else {
        reject(respApi.error);
      }
      dispatch(setCampaignListLoading(false));
    };
    dispatch(setCampaignListLoading(true));
    arClient.doApiCall('/campaign/pause', { campaignId }, callback);
  });
};

export const resumeCampaign = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const callback = (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        const newStatus = respApi.data?.newStatus || 'Active';
        dispatch(
          setCampaignList({
            list: getState().Campaigns.campaignList.map((campaign) =>
              // eslint-disable-next-line eqeqeq
              campaign.id == campaignId ? { ...campaign, status: newStatus } : campaign
            ),
          })
        );
        resolve(respApi.data);
      } else {
        reject(respApi.error);
      }
      dispatch(setCampaignListLoading(false));
    };

    dispatch(setCampaignListLoading(true));
    arClient.doApiCall('/campaign/resume', { campaignId }, callback);
  });
};

export const getROIDashboardData = (campaignId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      const { metrics, budgets } = respApi.data;
      dispatch(setROIDashboardData(Object.assign(metrics, budgets)));
      dispatch(setROIDashboardCampaignData(respApi.data));
      dispatch(setCampaignAccessError(null));
    } else {
      // Handle error responses (forbidden, not found, etc.)
      const errorMessage = respApi.error || respApi.message || 'Access denied to this campaign';
      dispatch(setCampaignAccessError(errorMessage));
    }
    dispatch(setROIDashboardDataLoading(false));
  };

  dispatch(setROIDashboardDataLoading(true));
  dispatch(setCampaignAccessError(null));

  arClient.doApiCall('/campaign/getROIDashboardByCampaignId', { campaignId }, callback);
};

const extractInterests = (data) => {
  try {
    return _.chain(data)
      .flatMap((item) => item.videos)
      .filter((video) => _.has(video, 'info.audience_interest_distribution'))
      .flatMap((video) => video.info.audience_interest_distribution)
      .map('interest_name')
      .compact()
      .sortBy()
      .value();
  } catch {
    return [];
  }
};

export const getReportingDashboardData = (campaignId) => async (dispatch) => {
  const callback2 = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setReportingDataResponce([]));
      dispatch(setReportingDataResponceV2(respApi.data));
      dispatch(setReportingDataInterests(extractInterests(respApi.data)));
    } else {
      // error handling here
    }
    dispatch(setReportingDataLoading(false));
  };

  dispatch(setReportingDataLoading(true));
  arClient.doApiCall('/tiktok-one/get-order-report', { campaignId }, callback2);
};

export const getReportingData = (campaignId) => {
  return new Promise((resolve) => {
    const callback = (respApi) => {
      if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
        resolve(processReportingData(respApi.data));
      } else {
        // Resolve with empty data instead of rejecting to prevent unhandled rejection
        // eslint-disable-next-line no-console
        console.error('API Failed: getReportingDataV2');
        resolve(processReportingData([]));
      }
    };

    arClient.doApiCall('/campaign/getReportingDataV2', { campaignId }, callback);
  });
};

export const getReportingDashboardCapsules = (campaignId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      const normalizedData = _.map(respApi.data, (capsule) => ({
        ...capsule,
        interestArray: _.castArray(
          _.isArray(capsule.interestArray)
            ? capsule.interestArray
            : _.attempt(() => JSON.parse(capsule.interestArray))
        ).filter(_.isString),
      }));

      dispatch(setReportingDataCapsules(normalizedData));
    } else {
      const status = respApi.status ?? 'Unknown';
      const messageText = respApi.error ?? 'API Error. Please try again.';

      dispatch(setReportingDataCapsules([])); // reset capsules if error occurs on BE

      displayNotification(
        'error',
        'API Failed: /campaign/getReportingCapsules',
        `${status}: ${messageText}`
      );
    }
    dispatch(setReportingDataLoadingCapsules(false));
  };

  dispatch(setReportingDataLoadingCapsules(true));
  arClient.doApiCall('/campaign/getReportingCapsules', { campaignId }, callback);
};

export const removeReportingDashboardCapsule = (capsuleId, campaignId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(getReportingDashboardCapsules(campaignId));
    } else {
      const status = respApi.status ?? 'Unknown';
      const messageText = respApi.error ?? 'API Error. Please try again.';

      displayNotification(
        'error',
        'API Failed: /campaign/deleteReportingCapsule',
        `${status}: ${messageText}`
      );
    }
  };

  dispatch(setReportingDataLoadingCapsules(true));
  arClient.doApiCall('/campaign/deleteReportingCapsule', { capsuleId }, callback);
};

export const updateReportingDashboardCapsule = (
  capsuleId,
  campaignId,
  capsuleName,
  interestArray
) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(getReportingDashboardCapsules(campaignId));
    } else {
      const status = respApi.status ?? 'Unknown';
      const messageText = respApi.error ?? 'API Error. Please try again.';

      displayNotification(
        'error',
        'API Failed: /campaign/updateReportingCapsule',
        `${status}: ${messageText}`
      );
    }
  };

  dispatch(setReportingDataLoadingCapsules(true));
  arClient.doApiCall(
    '/campaign/updateReportingCapsule',
    {
      capsuleId,
      campaignId,
      capsuleName,
      interestArray: JSON.stringify(interestArray),
    },
    callback
  );
};

export const addReportingDashboardCapsule = (campaignId, capsuleName, interestArray) => async (
  dispatch
) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(getReportingDashboardCapsules(campaignId));
    } else {
      const status = respApi.status ?? 'Unknown';
      const messageText = respApi.error ?? 'API Error. Please try again.';

      displayNotification(
        'error',
        'API Failed: /campaign/addReportingCapsule',
        `${status}: ${messageText}`
      );
    }
  };

  dispatch(setReportingDataLoadingCapsules(true));
  arClient.doApiCall(
    '/campaign/addReportingCapsule',
    {
      campaignId,
      capsuleName,
      interestArray: JSON.stringify(interestArray),
    },
    callback
  );
};

export const addUpdateCampaign = (values) => async (dispatch) => {
  const callback = (resp) => {
    dispatch(setIsCreateCampaignLoading(false));
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      dispatch(setLatestSavedCampaign(values.name));
    } else {
      dispatch(setErrMsg(resp.error));
    }
  };

  dispatch(setErrMsg(null));
  dispatch(setIsCreateCampaignLoading(true));

  arClient.doApiCall('/campaign/create', values, callback);
};

export const addToROIDashboard = (objParam) => async (dispatch) => {
  const callback = (resp) => {
    dispatch(
      setROIDashboardDataUpdated(
        resp.status === LEGACY_OK || resp.status === OK || resp.status === 200
      )
    );

    setTimeout(() => dispatch(setROIDashboardDataUpdated(null)), 9000); // resets
  };

  arClient.doApiCall('/campaign/addToROIDashboard', objParam, callback);
};

export const setRoiDashForecastBudget = (value) => async (dispatch) => {
  dispatch(setROIDashboardForecastBudgetData(value));
};

export const deleteCampaignById = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { Campaigns } = getState();
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const newList = Campaigns.campaignList.map((campaign) => {
          if (campaign.id === campaignId) {
            return {
              ...campaign,
              deleteRequestedAt: new Date(),
            };
          }
          return campaign;
        });
        dispatch(setCampaignList({ list: newList }));
        resolve();
      } else {
        reject();
      }
      dispatch(setCampaignListLoading(false));
    };
    dispatch(setCampaignListLoading(true));
    arClient.doApiCall('/campaign/deleteById', { campaignId }, callback);
  });
};

export const restoreCampaignById = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { Campaigns } = getState();

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const newList = Campaigns.campaignList.map((campaign) => {
          if (campaign.id === campaignId) {
            return {
              ...campaign,
              deleteRequestedAt: null,
            };
          }
          return campaign;
        });

        dispatch(setCampaignList({ list: newList }));
        resolve();
      } else {
        reject();
      }
      dispatch(setCampaignListLoading(false));
    };
    dispatch(setCampaignListLoading(true));
    arClient.doApiCall('/campaign/restorePendingDeleteById', { campaignId }, callback);
  });
};

export const deleteROICampaignById = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { Campaigns } = getState();

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(
          setROIDashboardCampaignData({
            campaign: {
              ...Campaigns.ROIDashboardCampaignData,
              deleteRequestedAt: new Date(),
            },
          })
        );
        resolve();
      } else {
        reject();
      }
      dispatch(setROIDashboardDataLoading(false));
    };
    dispatch(setROIDashboardDataLoading(true));
    arClient.doApiCall('/campaign/deleteById', { campaignId }, callback);
  });
};

export const restoreROICampaignById = (campaignId) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const { Campaigns } = getState();

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(
          setROIDashboardCampaignData({
            campaign: {
              ...Campaigns.ROIDashboardCampaignData,
              deleteRequestedAt: null,
            },
          })
        );
        resolve();
      } else {
        reject();
      }
      dispatch(setROIDashboardDataLoading(false));
    };
    dispatch(setROIDashboardDataLoading(true));
    arClient.doApiCall('/campaign/restorePendingDeleteById', { campaignId }, callback);
  });
};

export const fetchCampaignCreators = (campaignId) => (dispatch) => {
  if (_.isNil(campaignId)) {
    return;
  }

  dispatch(
    setCampaignCreatorData({
      key: 'fetchCampaignCreatorsLoading',
      value: true,
    })
  );
  const callback = (resp) => {
    if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
      dispatch(
        setCampaignCreatorData({
          key: 'campaignCreators',
          value: [resp.data, resp.indStdER],
        })
      );
      dispatch(
        setCampaignCreatorData({
          key: 'isCampaignCreatorsFetched',
          value: true,
        })
      );
      dispatch(
        setCampaignCreatorData({
          key: 'campaignCreatorsFetchedId',
          value: campaignId,
        })
      );
    }

    dispatch(
      setCampaignCreatorData({
        key: 'fetchCampaignCreatorsLoading',
        value: false,
      })
    );
  };

  arClient.doApiCall('/campaign/fetchCreators', { campaignId }, callback);
};

export const getOutreachBudgetData = (creatorReportDetailsId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setOutreachBudgetData(respApi.data));
    } else {
      // error handling here
    }
    dispatch(setOutreachBudgetDataLoading(false));
  };

  dispatch(setOutreachBudgetDataLoading(true));

  arClient.doApiCall('/creator/getBudget', { creatorReportDetailsId }, callback);
};

export const setOutreachBudget = (objParam) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setOutreachBudgetDataLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    dispatch(setOutreachBudgetDataLoading(true));

    arClient.doApiCall('/creator/setBudget', objParam, callback);
  });
};

export const setOutreachBudgets = (objParam) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setOutreachBudgetDataLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(
          setOutreachBudgetData([
            {
              creatorReportDetailsId: objParam.creatorReportDetailsId,
              asking: objParam?.asking || 0,
              agreed: objParam?.agreed || 0,
              remainingBudget: objParam?.remainingBudget || 0,
            },
          ])
        );
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    dispatch(setOutreachBudgetDataLoading(true));

    arClient.doApiCall('/creator/setBudgets', objParam, callback);
  });
};

export const getOutreachNotesData = (creatorReportDetailsId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setOutreachNotesData(respApi.data));
    } else {
      // error handling here
    }
    dispatch(setOutreachNotesDataLoading(false));
  };

  dispatch(setOutreachNotesDataLoading(true));

  arClient.doApiCall('/outreach/getInternalNotes', { creatorReportDetailsId }, callback);
};

export const setOutreachNotes = (creatorReportDetailsId, notesList) => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setOutreachNotesDataLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(getOutreachNotesData(creatorReportDetailsId));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    dispatch(setOutreachNotesDataLoading(true));

    arClient.doApiCall(
      '/outreach/setInternalNotes',
      {
        creatorReportDetailsId,
        notesArr: JSON.stringify(notesList),
      },
      callback
    );
  });
};

export const initOutreach = (creatorReportDetailsId) => (dispatch) => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const outreachData = _.cloneDeep(resp);
        const { contactDetails = [] } = resp;

        if (_.isNil(contactDetails)) {
          outreachData.contactDetails = [];
        }
        // populate emailTo field
        if (!contactDetails || contactDetails.length === 0) {
          outreachData.emailTo = [];
        } else {
          const emailToObj = contactDetails.find((contact) => contact.type === 'email');
          if (emailToObj) {
            outreachData.emailTo = [emailToObj.value];
          }
        }
        dispatch(setOutreachData(outreachData));
        resolve();
      } else {
        resolve();
      }
    };
    arClient.doApiCall('/outreach/init', { creatorReportDetailsId }, callback);
  });
};

export const sendOutreachEmail = ({
  outreachId,
  to,
  bcc = JSON.stringify([]),
  cc = JSON.stringify([]),
  subject,
  bodyHTML,
  parentGmailId = null,
  senderInfo = JSON.stringify({
    platform: 'default',
    oauthInfoId: null,
  }),
  isInitialEmail = false,
  attachmentsJSON = JSON.stringify([]),
}) => (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const sanitizedBodyHTML = stripFroalaAttribution(bodyHTML);

    const callback = (resp) => {
      if (
        resp.status === 'INIT' ||
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        resp.status === 200
      ) {
        const { Campaigns } = getState();
        const { outreachData } = Campaigns;
        const emails = _.isArray(outreachData.emails) ? _.cloneDeep(outreachData.emails) : [];

        // add newly added email to the email list
        emails.push({
          id: uuidv4(),
          createdTimeStamp: Date.now(),
          to,
          subject,
          bodyHTML: sanitizedBodyHTML,
        });
        dispatch(
          setOutreachData({
            ...outreachData,
            emails,
          })
        );
        resolve(true);
      } else {
        reject();
      }
    };

    const payload = {
      subject,
      bodyHTML: sanitizedBodyHTML,
      to,
      bcc,
      cc,
      outreachId,
      senderInfo,
      isInitialEmail,
      attachmentsJSON,
    };

    if (parentGmailId !== null) {
      payload.parentGmailId = parentGmailId;
    }
    arClient.doApiCall('/outreach/sendEmail', payload, callback);
  });
};

export const sendMassOutreachEmail = ({
  subject,
  bodyHTML,
  senderInfo = JSON.stringify({
    platform: 'default',
    oauthInfoId: null,
  }),
  creatorContacts = JSON.stringify([]),
}) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const sanitizedBodyHTML = stripFroalaAttribution(bodyHTML);

    const callback = (resp) => {
      if (
        resp.status === 'INIT' ||
        resp.status === 10 ||
        resp.status === 'OK' ||
        resp.status === 200
      ) {
        dispatch(setEmailMassOutreachList([]));
        resolve(true);
      } else {
        reject();
      }
    };

    const payload = {
      subject,
      bodyHTML: sanitizedBodyHTML,
      senderInfo,
      creatorContacts,
    };

    arClient.doApiCall('/outreach/massOutreach', payload, callback);
  });
};

export const fetchInfContentRevision = (contentRevisionId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    // Use public API call (no auth required for influencer pages)
    arClient.doPublicApiCall(
      '/custom/fetch',
      {
        contentRevisionId,
      },
      callback
    );
  });
};

export const fetchInfContentRevisionDetails = (contentRevisionDetailsId) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        // Response format: {status: 10, data: {details, revisionDetails, campaignAndCreatorDetails, ...}}
        const { data } = resp;
        if (data) {
          dispatch(setRevisionDetailsData(data));
          resolve();
        } else {
          reject(new Error('No data in response'));
        }
      } else {
        reject(new Error(resp.error || 'API error'));
      }
    };
    // Use public API call (no auth required for influencer pages)
    arClient.doPublicApiCall(
      '/custom/get-conversation',
      {
        contentRevisionDetailsId,
      },
      callback
    );
  });
};

export const addInfluencerResponse = ({ contentRevisionDetailsId, msgHTML, mediaLink = null }) => (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const sanitizedMsgHTML = stripFroalaAttribution(msgHTML);

    const callback = (resp) => {
      if (resp.status === 10 || resp.status === 'OK' || resp.status === 200) {
        resolve(resp.data);
        const { Campaigns } = getState();
        const { revisionDetailsData } = Campaigns;
        dispatch(fetchInfContentRevisionDetails(contentRevisionDetailsId));
        dispatch(
          setRevisionDetailsData({
            ...revisionDetailsData,
            details: resp.data,
          })
        );
      } else {
        reject();
      }
    };

    // Use public API call (no auth required for influencer pages)
    arClient.doPublicApiCall(
      '/custom/add-influencer-response',
      {
        contentRevisionDetailsId,
        parentId: null,
        msgHTML: sanitizedMsgHTML,
        mediaLink,
      },
      callback
    );
  });
};

export const infSubmitPublishURL = ({ contentRevisionDetailsId, publishURL }) => (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Campaigns } = getState();
        const { revisionDetailsData } = Campaigns;
        resolve(resp.data);
        dispatch(
          setRevisionDetailsData({
            ...revisionDetailsData,
            revisionDetails: {
              ...revisionDetailsData.revisionDetails,
              publishURL,
            },
          })
        );
      } else {
        reject();
      }
    };

    arClient.doApiCall(
      '/custom/add-publish-link',
      {
        contentRevisionDetailsId,
        publishURL,
      },
      callback
    );
  });
};

export const clientSubmitPublishURL = ({ contentRevisionDetailsId, publishURL }) => (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Campaigns } = getState();
        const { revisionDetailsData } = Campaigns;
        resolve(resp.data);
        dispatch(
          setRevisionDetailsData({
            ...revisionDetailsData,
            revisionDetails: {
              ...revisionDetailsData.revisionDetails,
              publishURL,
            },
          })
        );
      } else {
        reject();
      }
    };

    arClient.doApiCall(
      '/content-revision/addPublishLink',
      {
        contentRevisionDetailsId,
        publishURL,
      },
      callback
    );
  });
};

export const addUpdateCampaignV2 = (values) => (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setIsCreateCampaignLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data ?? resp);
      } else {
        reject();
      }
    };

    dispatch(setIsCreateCampaignLoading(true));
    arClient.doApiCall('/campaign/createV2', values, callback);
  });
};

export const getAgreementCount = (creatorReportDetailsId, campaignId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setInfluencerAgreementCount(respApi.data));
    } else {
      // error handling here
    }
    dispatch(setInfluencerAgreementCountLoading(false));
  };

  dispatch(setInfluencerAgreementCountLoading(true));

  arClient.doApiCall('/agreement/get', { creatorReportDetailsId, campaignId }, callback);
};

export const setAgreementCount = (creatorReportDetailsId, campaignId, agreementList) => async (
  dispatch
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setInfluencerAgreementCountLoading(false));
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setInfluencerAgreementCount(resp.data));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    dispatch(setInfluencerAgreementCountLoading(true));

    arClient.doApiCall(
      '/agreement/create',
      {
        creatorReportDetailsId,
        campaignId,
        contentJson: JSON.stringify(agreementList),
      },
      callback
    );
  });
};

export const sendRevisionNotificationEmails = (contentRevisionId, recipients, bodyHTML) => {
  return new Promise((resolve, reject) => {
    const sanitizedBodyHTML = stripFroalaAttribution(bodyHTML);

    const callback = (resp) => {
      if (resp.status === 10 || resp.status === 'OK' || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/content-revision/sendNotificationEmails',
      {
        contentRevisionId,
        to: JSON.stringify(recipients),
        bodyHTML: sanitizedBodyHTML,
        subject: 'Content Revision Link',
      },
      callback
    );
  });
};

export const updateAgreementContent = (creatorReportDetailsId, campaignId, agreementList) => async (
  dispatch
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      dispatch(setInfluencerAgreementCountLoading(false));
      if (resp.status === 10 || resp.status === 'OK' || resp.status === 200) {
        dispatch(setInfluencerAgreementCount(resp.data));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    dispatch(setInfluencerAgreementCountLoading(true));

    arClient.doApiCall(
      '/agreement/updateContentJson',
      {
        creatorReportDetailsId,
        campaignId,
        contentJson: JSON.stringify(agreementList),
      },
      callback
    );
  });
};

export const getOutreachEmailAddresses = (creatorReportDetailsId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setOutreachEmailAddressesData(respApi.data));
    } else {
      // error handling here
    }
    dispatch(setOutreachEmailAddressesDataLoading(false));
  };

  dispatch(setOutreachEmailAddressesDataLoading(true));

  arClient.doApiCall('/outreach/getEmailAddresses', { creatorReportDetailsId }, callback);
};

export const addOutreachEmailAddresses = (creatorReportDetailsId, emailsArr, id) => async (
  dispatch,
  getState
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Campaigns } = getState();
        const { outreachEmailAddresses } = Campaigns;
        let updatedEmails;
        if (id) {
          updatedEmails = outreachEmailAddresses.map((item) =>
            item.id === id ? { ...item, role: emailsArr[0].role, email: emailsArr[0].email } : item
          );
        } else {
          updatedEmails = Array.isArray(outreachEmailAddresses)
            ? [...outreachEmailAddresses, ...emailsArr]
            : [...emailsArr];
        }
        dispatch(setOutreachEmailAddressesData(updatedEmails));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/addEmailAddresses',
      {
        creatorReportDetailsId,
        emailsArr: JSON.stringify(emailsArr),
      },
      callback
    );
  });
};

export const fetchRevisionAnalysis = (revisionConvId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        if (_.isEmpty(resp.data)) {
          resolve({
            respData: null,
            status: null,
          });
          return;
        }
        const parsedRespData = JSON.parse(resp.data[0].respData);
        const formattedData = {
          respData: {
            analysis: parsedRespData.Posts['0'],
            meta: parsedRespData.Posts.meta,
          },
          status: resp.data[0].status,
        };
        resolve(formattedData);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/custom/fetch-revision-analysis',
      {
        revisionConvId,
      },
      callback
    );
  });
};

export const deleteOutreachEmailAddress = (id) => async (dispatch, getState) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const { Campaigns } = getState();
        const { outreachEmailAddresses } = Campaigns;
        const filteredEmailList = outreachEmailAddresses.filter((item) => item.id !== id);
        dispatch(setOutreachEmailAddressesData(filteredEmailList));
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/delEmailAddress',
      {
        id,
      },
      callback
    );
  });
};

export const getOutreachEmailCount = (outreachId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/emailCount',
      {
        outreachId,
      },
      callback
    );
  });
};

export const rerunRevisionAnalysis = (contentRevisionConversationId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/content-revision/rerunAnalysis',
      {
        contentRevisionConversationId,
      },
      callback
    );
  });
};

export const authorizeOutlookAccount = (authCode, redirectURI) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/outreach/authorizeOutlookEmailAddress',
      {
        authCode,
        redirectURI,
      },
      callback
    );
  });
};

export const fetchAllSenderEmails = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const emails = (resp.data || []).map((row) => ({
          ...row,
          platform: normalizeSenderPlatform(row.platform),
        }));

        emails.unshift({
          email: OUTREACH_DEFAULT_EMAIL,
          platform: 'default',
          oauthInfoId: null,
        });

        resolve(emails);
      } else {
        reject(resp);
      }
    };

    // return;
    arClient.doApiCall(
      '/outreach/getSenderEmailAddresses',
      {
        campaignId,
      },
      callback
    );
  });
};

export const removeOAuthConnection = (oAuthInfoDetailsId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (
        resp.status === LEGACY_OK ||
        resp.status === OK ||
        (resp.status === 200 && resp.data === true)
      ) {
        resolve(true);
      } else {
        reject();
      }
    };

    // return;
    arClient.doApiCall(
      '/outreach/removeOAuthConnection',
      {
        oAuthInfoDetailsId,
      },
      callback
    );
  });
};

export const getMagicLink = (builderRequestId, segmentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/magic-link/fetch',
      {
        builderRequestId,
        segmentId,
      },
      callback
    );
  });
};

export const getInfListByMagicLink = (magicLinkId, hashCode) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/magic-link/getInfList',
      {
        magicLinkId,
        hashCode,
      },
      callback
    );
  });
};

// `clientRationale` is optional: it is only sent when provided (as `client_rationale`), so callers
// that omit it (the legacy Persona magic link) send exactly the same body as before.
export const createMagicLink = (builderRequestId, segmentId, expiryDate, clientRationale) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/magic-link/create',
      {
        expiryDate,
        builderRequestId,
        segmentId,
        ...(clientRationale !== undefined && { client_rationale: clientRationale }),
      },
      callback
    );
  });
};

export const removeMagicLink = (magicLinkId, hashCode) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/magic-link/remove',
      {
        magicLinkId,
        hashCode,
      },
      callback
    );
  });
};

// `clientRationale` is optional — see createMagicLink.
export const updateMagicLink = (magicLinkId, hashCode, expiryDate, clientRationale) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/magic-link/update',
      {
        magicLinkId,
        hashCode,
        expiryDate,
        ...(clientRationale !== undefined && { client_rationale: clientRationale }),
      },
      callback
    );
  });
};

const postPricingCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const clearPostPricingCache = () => {
  postPricingCache.clear();
};

export const clearPostPricingCacheEntry = (requestedPrice, creatorReportDetailsId) => {
  const cacheKey = `${requestedPrice}_${creatorReportDetailsId}`;
  postPricingCache.delete(cacheKey);
};

export const getPostPricing = (requestedPrice, creatorReportDetailsId) => {
  return new Promise((resolve, reject) => {
    const cacheKey = `${requestedPrice}_${creatorReportDetailsId}`;
    const now = Date.now();

    if (postPricingCache.has(cacheKey)) {
      const cachedData = postPricingCache.get(cacheKey);
      if (now - cachedData.timestamp < CACHE_DURATION) {
        resolve(cachedData.data);
        return;
      }
      postPricingCache.delete(cacheKey);
    }

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        postPricingCache.set(cacheKey, {
          data: resp.data,
          timestamp: now,
        });
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/creator/getPostPricing',
      {
        requestedPrice,
        creatorReportDetailsId,
      },
      callback
    );
  });
};

// Docusign endpoints

// This endpoint will check is for campaign docusign account exists and will return account details.
export const getDocusignInfo = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/docusign/user-info',
      {
        campaignId,
      },
      callback
    );
  });
};

// This endpoint will return all account templates
export const getDocusignTemplates = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === 10 || resp.status === 'OK' || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/docusign/get-templates',
      {
        campaignId,
      },
      callback
    );
  });
};

// This endpoint will send the proposal
export const createEnvelope = (
  campaignId,
  docusignAccountId,
  emailSubject,
  emailBlurb,
  templateId,
  signers,
  creatorReportDetailsId
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/docusign/create-envelope-from-template',
      {
        campaignId,
        docusignAccountId,
        emailSubject,
        emailBlurb,
        templateId,
        signers: JSON.stringify(signers),
        creatorReportDetailsId,
      },
      callback
    );
  });
};

// this endpoint get the docusign envelopes list
export const getDocusignEnvelopes = (campaignId, creatorReportDetailsId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      dispatch(setEnvelopesList(respApi.data.envelopes));
    }
    dispatch(setEnvelopesListLoading(false));
  };

  dispatch(setEnvelopesListLoading(true));

  arClient.doApiCall(
    '/docusign/get-list-envelopes',
    { campaignId, creatorReportDetailsId },
    callback
  );
};

// This endpoint will return embed URL array for each recipient
export const getDocusignEmbedURL = (campaignId, envelopeId, currentUrl) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/docusign/envelope-embed-view',
      {
        campaignId,
        envelopeId,
        localUri: currentUrl,
      },
      callback
    );
  });
};

// Google docs endpoints

// This endpoint will check is for campaign docusign account exists and will return account details, if not exists you need to run endpoint 1 and 2.
export const getGoogleDocsInfo = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/google-doc/user-info',
      {
        campaignId,
      },
      callback
    );
  });
};

// This endpoint will create new document in google docs
export const createDocument = (
  campaignId,
  documentName,
  S3Url,
  creatorReportDetailsId,
  emailList
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/google-doc/create-document',
      {
        campaignId,
        documentName,
        ...(S3Url && { S3Url }),
        creatorReportDetailsId,
        emailList: btoa(JSON.stringify(emailList)),
      },
      callback
    );
  });
};

// this endpoint get the docusign envelopes list
export const getGoogleDocsDocuments = (campaignId, creatorReportDetailsId) => async (dispatch) => {
  const callback = (respApi) => {
    if (respApi.status === 10 || respApi.status === 'OK' || respApi.status === 200) {
      const docs = respApi.data?.data || respApi.data || [];
      dispatch(setGoogleDocumentList(Array.isArray(docs) ? docs : []));
    }
    dispatch(setGoogleDocumentListLoading(false));
  };

  dispatch(setGoogleDocumentListLoading(true));

  arClient.doApiCall('/google-doc/all-documents', { campaignId, creatorReportDetailsId }, callback);
};

export const getAllContractingDocuments = () => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/contracting/listDocumentsWithRecipients', {}, callback);
  });
};

export const deleteContractingDocument = (documentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/contracting/deleteDocument', { id: documentId }, callback);
  });
};

export const duplicateContractingDocument = (documentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/contracting/duplicateDocument', { id: documentId }, callback);
  });
};

export const renameContractingDocument = (documentId, newName) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/contracting/renameDocument',
      { id: documentId, documentName: newName },
      callback
    );
  });
};

export const editContractingDocument = (documentId, newName) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/contracting/editDocument', { documentId, newName }, callback);
  });
};

// Update document content
export const updateContractingDocument = (payload) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/contracting/updateDocument', payload, callback);
  });
};

// This endpoint will generate PDf file and upload to our S3 bucket
export const exportPDFDocument = (campaignId, documentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/google-doc/export-document',
      {
        campaignId,
        documentId,
      },
      callback
    );
  });
};

// This endpoint will grant access to document for influcencer
export const grantGoogleAccess = (campaignId, documentId, creatorEmail) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/google-doc/grant-access',
      {
        campaignId,
        documentId,
        creatorEmails: btoa(JSON.stringify(creatorEmail)),
      },
      callback
    );
  });
};

// This endpoint create the agreement for docusign from google docs

export const createGoogleDocusignAgreement = (
  campaignId,
  docusignAccountId,
  emailSubject,
  signers,
  googleDocumentID
) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/google-doc/sign-with-docusign',
      {
        campaignId,
        docusignAccountId,
        emailSubject,
        signers: JSON.stringify(signers),
        googleDocumentID,
      },
      callback
    );
  });
};

// Get e-commerce categories
export const getEcommerceCategories = () => async (dispatch) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        dispatch(setEcommerceCategoriesList(resp.data));
        dispatch(setEcommerceCategoriesListLoading(false));
        resolve();
      } else {
        dispatch(setEcommerceCategoriesListLoading(false));
        reject(resp);
      }
    };
    dispatch(setEcommerceCategoriesListLoading(true));
    arClient.doApiCall('/e-commerce/getCategories', {}, callback);
  });
};

// This endpoint upload files to S3
export const uploadFileToS3 = (folder, bucket, mediaJson) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall(
      '/media-model/s3-upload',
      { folder, bucket, mediaJson: JSON.stringify(mediaJson) },
      callback,
      null, // doEncodeURIComponent
      { timeout: 120000 } // 2 minute timeout for large file uploads
    );
  });
};

// Authenticated multipart/form-data upload to the same endpoint: the file is sent as raw
// binary (no base64 inflation) and streamed to S3 by the API. Expects FormData with
// `file` (+ optional `thumbnail`, `folder`, `bucket`). Resolves like uploadFileToS3.
export const uploadFileToS3Multipart = (formData) => {
  return new Promise((resolve, reject) => {
    arClient.doApiCall(
      '/media-model/s3-upload',
      formData,
      (resp) => {
        if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
          resolve(resp);
        } else {
          reject(resp);
        }
      },
      null, // doEncodeURIComponent
      { timeout: 30 * 60 * 1000 } // large videos on slow connections; covers the whole upload
    );
  });
};

// Upload files to S3 from influencer side (public — no auth required)
// Uses multipart/form-data to avoid base64 inflation (supports large video uploads)
export const uploadFileToS3Influencer = (formData) => {
  return new Promise((resolve, reject) => {
    arClient.doPublicApiCall('/custom/upload', formData, (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    });
  });
};

export const linkWithTTO = (TTOCampaignId, videoId, requestsAction) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp.error);
      }
    };

    arClient.doApiCall(
      '/tiktok-one/campaignVideoLink',
      { TTOCampaignId, videoId, requestsAction },
      callback
    );
  });
};

export const getTTOLinkingStatus = (TTOCampaignId, videoId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/tiktok-one/videoStatus', { TTOCampaignId, videoId }, callback);
  });
};

export const getContractingTemplates = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        const docs = resp.data?.data || resp.data || [];
        resolve(Array.isArray(docs) ? docs : []);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/contracting/getTemplates', { campaignId }, callback);
  });
};

export const createContractingDocument = (documentType, documentName, html, isTemplate = 1) => {
  return new Promise((resolve, reject) => {
    const sanitizedHtml = stripFroalaAttribution(html);

    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall(
      '/contracting/createDocument',
      { documentType, documentName, html: sanitizedHtml, isTemplate },
      callback
    );
  });
};

export const sendNDAToInfluencer = (documentId, creatorReportDetailsId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall(
      '/contracting/sendNdaToInfluencer',
      { documentId, creatorReportDetailsId },
      callback
    );
  });
};

export const sendCreativeBriefToInfluencer = (documentId, creatorReportDetailsId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall(
      '/contracting/sendCreativeBriefToInfluencer',
      { documentId, creatorReportDetailsId },
      callback
    );
  });
};

export const generateNDAPDF = (documentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };
    arClient.doApiCall('/contracting/generatePdf', { documentId }, callback);
  });
};

export const getImageBase64 = (imageURL) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/outreach/getImage', { imageURL }, callback);
  });
};

export const getSignedUrl = (fileURL) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data?.signedUrl || resp.data?.data?.signedUrl);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/outreach/getSignedUrl', { fileURL }, callback);
  });
};

export const createSegments = (payload) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/persona/generateSegments', payload, callback, null, { timeout: 300000 });
  });
};

export const generateMoreAudiences = (payload) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/persona/generateMoreSegments', payload, callback, null, {
      timeout: 300000,
    });
  });
};

export const submitAudienceGeneration = (payload) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/persona/saveSegmentUserSelection', payload, callback);
  });
};

export const generateAudienceInfluencerList = (payload) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/persona/getSegmentInfluencers', payload, callback);
  });
};

export const getPipelineStatus = (personaBuilderRequestId, segmentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/creator/pipelineStatus', { personaBuilderRequestId, segmentId }, callback);
  });
};

export const retryPipelineStep = (personaBuilderRequestId, segmentId, steps) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    // URLSearchParams flattens arrays — use steps[0], steps[1] format for Laravel
    const params = { personaBuilderRequestId, segmentId };
    steps.forEach((step, i) => {
      params[`steps[${i}]`] = step;
    });

    arClient.doApiCall('/creator/retryPipelineStep', params, callback);
  });
};

export const seedEnrichment = (personaBuilderRequestId, segmentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/creator/seedEnrichment', { personaBuilderRequestId, segmentId }, callback);
  });
};

export const fetchPipelineStatusByCampaign = (campaignId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/creator/pipelineStatusByCampaign', { campaignId }, callback);
  });
};

export const getCreatorPipelineStatus = (personaBuilderRequestId, segmentId) => {
  return new Promise((resolve, reject) => {
    const callback = (resp) => {
      if (isApiSuccess(resp)) {
        resolve(resp.data ?? null);
      } else {
        reject(resp);
      }
    };

    arClient.doApiCall('/creator/pipelineStatus', { personaBuilderRequestId, segmentId }, callback);
  });
};

export const getCampaignOverview = (campaignId) => (dispatch) => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data);
        dispatch(setCampaignOverview(resp.data));
      } else {
        // Don't log cancelled requests as errors
        if (!resp.isCancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch campaign overview:', resp);
        }
        // Resolve with empty data instead of rejecting to prevent unhandled promise errors
        resolve({});
        dispatch(setCampaignOverview({}));
      }
    };

    arClient.doApiCall('/campaign/getOverviewV2', { campaignId }, callback);
  });
};

export const getPlatformOverview = (campaignId, metrics = 'engagementRate', dateRange = 'all') => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data.platforms);
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch platform overview:', resp);
        resolve([]);
      }
    };

    arClient.doApiCall(
      '/campaign/getPlatformOverview',
      { campaignId, metrics, dateRange },
      callback
    );
  });
};

export const getCampaignInsightsOverview = (campaignId, dateRange = 'all') => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data.insights);
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch campaign insights overview:', resp);
        resolve({});
      }
    };

    arClient.doApiCall('/campaign/getInsightsOverview', { campaignId, dateRange }, callback);
  });
};

export const getBestPerformingPosts = (
  campaignId,
  sortBy = 'engagementRate',
  platform = 'all',
  dateRange = 'all'
) => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data.bestPosts);
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch best performing posts:', resp);
        resolve([]);
      }
    };

    arClient.doApiCall(
      '/campaign/getBestPerformingPosts',
      { campaignId, sortBy, platform, dateRange },
      callback
    );
  });
};

export const getBestPerformingInfluencers = (
  campaignId,
  sortBy = 'engagementRate',
  platform = 'all',
  dateRange = 'all'
) => {
  return new Promise((resolve) => {
    const callback = (resp) => {
      if (resp.status === LEGACY_OK || resp.status === OK || resp.status === 200) {
        resolve(resp.data.influencers);
      } else {
        // eslint-disable-next-line no-console
        console.error('Failed to fetch best performing influencers:', resp);
        resolve([]);
      }
    };

    arClient.doApiCall(
      '/campaign/getInfluencersList',
      { campaignId, sortBy, platform, dateRange },
      callback
    );
  });
};

export default CampaignsSlice.reducer;
